import React, { useState, useEffect } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { categoryService } from '../services/categoryService';
import {
  CATEGORY_MAPPING,
  groupImagesByProduct,
  bulkUploadProducts
} from '../utils/bulkUploadHelper';

const BulkUploadModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState('select'); // select, mapping, uploading, complete
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getAllCategories();
      setCategories(data);

      // Auto-map categories if they exist
      const autoMap = {};
      Object.keys(CATEGORY_MAPPING).forEach(prefix => {
        const categoryInfo = CATEGORY_MAPPING[prefix];
        const matchedCategory = data.find(
          cat => cat.categoryName.toLowerCase() === categoryInfo.categoryName.toLowerCase()
        );
        if (matchedCategory) {
          autoMap[prefix] = matchedCategory._id;
        }
      });
      setCategoryMap(autoMap);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setError('Failed to fetch categories. Please try again.');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(files);
    setError(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleCategoryMapping = (prefix, categoryId) => {
    setCategoryMap(prev => ({
      ...prev,
      [prefix]: categoryId
    }));
  };

  const getImageGroups = () => {
    if (selectedFiles.length === 0) return {};
    return groupImagesByProduct(selectedFiles);
  };

  const getCategoryStats = () => {
    const groups = getImageGroups();
    const stats = {};

    Object.values(groups).forEach(group => {
      const category = group.category;
      if (!stats[category]) {
        stats[category] = { count: 0, images: 0 };
      }
      stats[category].count += 1;
      stats[category].images += group.images.length;
    });

    return stats;
  };

  const validateMapping = () => {
    const stats = getCategoryStats();
    const requiredCategories = Object.keys(stats);

    for (const category of requiredCategories) {
      if (!categoryMap[category]) {
        return false;
      }
    }
    return true;
  };

  const handleStartUpload = async () => {
    if (!validateMapping()) {
      setError('Please map all categories before uploading');
      return;
    }

    setStep('uploading');
    setError(null);

    try {
      const results = await bulkUploadProducts(
        selectedFiles,
        categoryMap,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      setUploadResults(results);
      setStep('complete');

      if (onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      console.error('Bulk upload failed:', error);
      setError(error.message || 'Bulk upload failed. Please try again.');
      setStep('mapping');
    }
  };

  const handleNextStep = () => {
    if (step === 'select') {
      if (selectedFiles.length === 0) {
        setError('Please select images to upload');
        return;
      }
      setStep('mapping');
    } else if (step === 'mapping') {
      handleStartUpload();
    }
  };

  const renderSelectStep = () => (
    <div>
      <h2 className="text-xl font-semibold mb-4">Select Scraped Images</h2>
      <p className="text-gray-600 mb-4">
        Select all images from the scraped-images folder. The system will automatically group them by category and product.
      </p>

      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-700 mb-2">Drag & drop images or click to browse</p>
        <p className="text-sm text-gray-500 mb-4">
          Select multiple images from scraped-images folder
        </p>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id="file-input"
        />
        <label
          htmlFor="file-input"
          className="inline-block px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 cursor-pointer"
        >
          Browse Files
        </label>
      </div>

      {selectedFiles.length > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium">
            {selectedFiles.length} images selected
          </p>
          <p className="text-sm text-green-600 mt-1">
            {Object.keys(getCategoryStats()).length} categories detected
          </p>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}
    </div>
  );

  const renderMappingStep = () => {
    const stats = getCategoryStats();

    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">Map Categories</h2>
        <p className="text-gray-600 mb-4">
          Map each detected category to an existing category in your database. {Object.keys(stats).length} categories detected from {selectedFiles.length} images.
        </p>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(stats).map(([prefix, stat]) => {
            const categoryInfo = CATEGORY_MAPPING[prefix];
            return (
              <div key={prefix} className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {categoryInfo?.categoryName || prefix}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {stat.count} products, {stat.images} images
                    </p>
                  </div>
                  {categoryMap[prefix] && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                </div>

                <select
                  value={categoryMap[prefix] || ''}
                  onChange={(e) => handleCategoryMapping(prefix, e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category._id} value={category._id}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>

                {categoryInfo && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">{categoryInfo.description}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!validateMapping() && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-yellow-800">Please map all categories to continue</p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}
      </div>
    );
  };

  const renderUploadingStep = () => {
    if (!uploadProgress) return null;

    const percentage = Math.round((uploadProgress.current / uploadProgress.total) * 100);

    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">Uploading Products</h2>
        <p className="text-gray-600 mb-6">
          Please wait while we upload your products. This may take a few minutes.
        </p>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {uploadProgress.stage === 'uploading_images' ? 'Uploading Images' : 'Creating Products'}
              </span>
              <span className="text-sm text-gray-600">
                {uploadProgress.current} / {uploadProgress.total}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {percentage}% complete
            </p>
          </div>

          {uploadProgress.productName && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Processing: {uploadProgress.productName}
                </p>
                {uploadProgress.imageProgress && (
                  <p className="text-xs text-blue-700 mt-1">
                    Image {uploadProgress.imageProgress.current} of {uploadProgress.imageProgress.total}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCompleteStep = () => {
    if (!uploadResults) return null;

    return (
      <div>
        <div className="text-center mb-6">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Upload Complete!</h2>
          <p className="text-gray-600">
            Your products have been uploaded successfully
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <p className="text-3xl font-bold text-green-700">{uploadResults.success.length}</p>
            <p className="text-sm text-green-600">Successfully Uploaded</p>
          </div>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
            <p className="text-3xl font-bold text-red-700">{uploadResults.failed.length}</p>
            <p className="text-sm text-red-600">Failed</p>
          </div>
        </div>

        {uploadResults.failed.length > 0 && (
          <div className="max-h-48 overflow-y-auto">
            <h3 className="font-medium text-gray-900 mb-2">Failed Uploads:</h3>
            <div className="space-y-2">
              {uploadResults.failed.map((fail, index) => (
                <div key={index} className="p-3 bg-red-50 border border-red-200 rounded text-sm">
                  <p className="text-red-800">{fail.error}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Bulk Upload Products</h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {step === 'select' && renderSelectStep()}
          {step === 'mapping' && renderMappingStep()}
          {step === 'uploading' && renderUploadingStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>

        <div className="p-6 border-t flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={step === 'uploading'}
          >
            {step === 'complete' ? 'Close' : 'Cancel'}
          </button>

          {step !== 'complete' && step !== 'uploading' && (
            <button
              onClick={handleNextStep}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                (step === 'select' && selectedFiles.length === 0) ||
                (step === 'mapping' && !validateMapping())
              }
            >
              {step === 'select' ? 'Next' : 'Start Upload'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
